import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { resolveDisplayPrice, type PdpVariantData } from '../shopware/pdpData';
import { checkProductAvailability, getFormattedReferencePrice } from '../shopware/shopwareUtils';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import type { AddToCartProduct, Variant } from './addToCartData';
import { VariantSelector } from './VariantSelector';

interface AddToCartModalProps {
  id: string;
  product: AddToCartProduct;
  variants: Variant[];
  selected: Variant;
  onSelect: (variant: Variant) => void;
  pdp: Record<string, PdpVariantData>;
  adding: boolean;
  /** Resolves true when the product landed in the cart; the modal then fades out and closes itself */
  onAdd: (variant: Variant, quantity: number) => Promise<boolean>;
  /** Called after the fade-out finished: unmount the modal */
  onClose: () => void;
}

const QUANTITIES = Array.from({ length: 10 }, (_, i) => i + 1);
// Matches Bootstrap's .modal.fade transition (transform .3s, opacity .15s)
const FADE_DURATION_MS = 300;

/**
 * Variant + quantity picker, rendered by the island into document.body while open.
 * Native <dialog> (focus trap, Escape, backdrop for free) dressed in Bootstrap's .modal.fade classes,
 * so Preact stays the only one writing to this DOM. `.e-shop` scopes the shop styles.
 * Open/close animate like the former Bootstrap modal: `show` is toggled one frame after showModal()
 * and removed FADE_DURATION_MS before onClose() unmounts us (the backdrop follows via CSS, see _add-to-cart.scss).
 */
export const AddToCartModal = ({ id, product, variants, selected, onSelect, pdp, adding, onAdd, onClose }: AddToCartModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closingRef = useRef(false);
  const [show, setShow] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setShow(false);
    window.setTimeout(onClose, FADE_DURATION_MS);
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    // Focus the container, like Bootstrap's modal did; Tab then moves to the close button as the first
    // control. `autofocus` on the dialog makes showModal() pick the dialog itself instead of the close
    // button, and preventScroll keeps either focus from scrolling the dialog towards the still
    // off-screen sheet on mobile (see the overflow note in _add-to-cart.scss).
    dialog.focus({ preventScroll: true });
    // next frame: add .show so the Bootstrap fade/slide transition runs from the initial state
    const frame = requestAnimationFrame(() => setShow(true));
    // Escape: keep the dialog open, run our fade-out first
    const handleCancel = (event: Event) => {
      event.preventDefault();
      requestClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(frame);
      dialog.removeEventListener('cancel', handleCancel);
      document.body.style.overflow = previousOverflow;
    };
  }, [requestClose]);

  const handleAdd = async () => {
    const added = await onAdd(selected, quantity);
    if (added) requestClose();
  };

  const variantData = pdp[selected.packsize.oNumber] ?? null;
  const shopwareProduct = variantData?.product ?? null;
  const available = checkProductAvailability(shopwareProduct);
  // Same price display as the PDP minus promo codes: code-free promotion or rule list price as Streichpreis
  // (resolveDisplayPrice), reference price unless hidden via PIM.
  // No price (e.g. variant not available): leave the price row empty; CSS keeps its height so the layout stays put.
  const { unitPrice, listPrice } = resolveDisplayPrice(variantData);
  const referencePrice = getFormattedReferencePrice(shopwareProduct);
  const hideBasePrice = selected.packsize.hideBasePrice === true;
  // Pack size packshot only, no fallback image
  const image = selected.packsize.packshotUrl || null;
  const titleId = `${id}-title`;

  return (
    <dialog
      ref={dialogRef}
      className={`add-to-cart-modal modal fade d-block${show ? ' show' : ''}`}
      tabIndex={-1}
      autoFocus
      aria-labelledby={titleId}
      onClick={(event) => {
        // .modal-dialog has pointer-events: none, so a click that reaches the dialog itself is a backdrop click
        if (event.target === dialogRef.current) requestClose();
      }}
    >
      <div className="e-shop modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body p-4">
            <button type="button" className="btn-close position-absolute top-0 end-0 m-4" aria-label="Schließen" onClick={requestClose} />
            <a href={product.url || undefined} className="d-flex gap-3 align-items-end mb-4 pb-2 text-body">
              <div className="add-to-cart-modal-visual bg-gray-200 flex-shrink-0 p-4">
                {image && <img src={image} alt={`Produktabbildung: ${product.name}`} />}
              </div>
              <div className="add-to-cart-modal-words d-flex flex-column">
                <h5 className="lh-1 mb-3 pe-4" id={titleId}>
                  {product.name}
                </h5>
                {product.description && (
                  <div className="small mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} />
                )}
                <div className="d-flex align-items-baseline lh-1">
                  <strong className="price text-nowrap">
                    {listPrice && <s className="price-original me-2">{listPrice}</s>}
                    <span className={listPrice ? 'price-discounted' : ''}>{unitPrice ?? ''}</span>
                  </strong>
                  {!hideBasePrice && referencePrice && <small>({referencePrice})</small>}
                </div>
              </div>
            </a>
            <VariantSelector
              id={`${id}-variants`}
              variants={variants}
              selectedONumber={selected.packsize.oNumber}
              onSelect={onSelect}
            />
            <div className="add-to-cart-modal-buttons d-flex gap-3">
              <select
                className="form-select w-auto"
                aria-label="Menge"
                value={quantity}
                onChange={(event) => setQuantity(Number((event.currentTarget as HTMLSelectElement).value))}
              >
                {QUANTITIES.map((amount) => (
                  <option key={amount} value={amount}>
                    {amount}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`btn btn-block ${available ? 'btn-primary' : 'btn-secondary'}`}
                disabled={!available || adding}
                aria-busy={adding}
                onClick={handleAdd}
              >
                {available ? 'In den Warenkorb' : <span className="out-of-stock">Bald wieder verfügbar</span>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};
